import { Product } from "@omni/types/api/product/bff/product.model";
import { ProductFieldSet } from "@omni/types/api/product/bff/shared/enums/productFieldSet.enum";
import { ProductTileLayout } from "@omni/ui/brand/product/productTile/productTile.config";
import { Anchor } from "@omni/ui/components/Anchor/anchor.component";
import { Button } from "@omni/ui/components/Button/button.component";
import { attempt } from "@omni/utils/function.utils";
import { isDefined } from "@omni/utils/helper.utils";
import * as LocalStorage from "@omni/utils/localStorage.utils";
import { cn } from "@omni/utils/styles";
import queryString from "query-string";
import {
    ReactElement,
    RefCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";
import { FreeMode, Mousewheel } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { Swiper as SwiperCore } from "swiper/types";
import Tweezer from "tweezer.js";

import { useBFFProductServiceApi } from "@/api/hooks/productService.hook";
import { ProductComparisonSkeleton } from "@/components/Product/ProductComparison/ProductComparisonSkeleton/ProductComparisonSkeleton";
import { ProductTile } from "@/components/Product/ProductTile/productTile.container";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { StorageKeys } from "@/utils/enums";
import { updateQueryVariable } from "@/utils/url/queryParams";

import { ProductTileRenditions } from "../ProductImage/mappedRenditions";
import { Actions } from "./Actions/actions.container";
import { Features } from "./Features/features.container";
import { ProductComparisonContextProvider } from "./productComparison.context";

export interface ProductComparisonContainerProps {
    productsPagePath: string;
    allowInstantAddToCart: boolean;
}

const ProductComparisonContainer = ({
    productsPagePath,
    allowInstantAddToCart
}: ProductComparisonContainerProps): ReactElement | null => {
    const { t } = useGranite();

    const [differenceOnly, setDifferenceOnly] = useState<boolean>(false);
    const [skus, setSkus] = useState<Array<string>>([]);

    const [allowPrevious, setAllowPrevious] = useState(false);
    const [allowNext, setAllowNext] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);

    const animating = useRef(false);
    const swiper = useRef<SwiperCore | null>(null);

    const featuresRef = useRef<HTMLDivElement | null>(null);

    const pinTriggerRef = useRef<HTMLDivElement>(null);
    const unpinTriggerRef = useRef<HTMLDivElement>(null);

    const [pinned, setPinned] = useState(false);

    const [{ data, loading }, getProducts] = useBFFProductServiceApi<
        any,
        any,
        Array<Product<ProductFieldSet.ProductTile>>
    >(
        {
            url: `/products`,
            method: "GET"
        },
        { manual: true, autoCancel: false }
    );

    const products = useMemo(
        () => data?.filter((p) => skus.some((s) => p.sku === s)) ?? [],
        [data, skus]
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        if (!skus?.length) {
            return;
        }

        setInitialLoading(false);
        if (skus.every((s) => products?.some((p) => p.sku === s))) {
            return;
        }
        getProducts({
            params: {
                sku: skus.join(","),
                fields: ProductFieldSet.ProductTile
            }
        });
    }, [skus]);

    useEffect(() => {
        const { products, showOnlyDifference } = queryString.parse(
            location.search
        );

        setDifferenceOnly(showOnlyDifference === "true");

        /**
         * We have to handle the scenario where the user arrives at this page from the
         * search page, in this case there will be no query params.
         *
         * To handle this situation, we do the following:
         * - Check the sku list in the query params.
         * - If it is empty, check the one in localStorage.
         * - If it is filled up, set the correct skuList and populate the query params.
         */
        const localStorageSkuList = attempt(
            () =>
                JSON.parse(
                    LocalStorage.getItem(
                        StorageKeys.ProductComparisonSkuList
                    ) || "[]"
                ),
            []
        );
        if (!products && localStorageSkuList.length) {
            updateQueryVariable("products", localStorageSkuList.join(","));
            return setSkus(localStorageSkuList);
        }

        setSkus((products as string)?.split(",") || []);
    }, []);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        if (!data) return;

        featuresRef.current?.addEventListener("scroll", syncProductsScroll);

        const intersectsBottom = (e: IntersectionObserverEntry) =>
            e.boundingClientRect.bottom >= window.outerHeight;

        const pinObserver = new IntersectionObserver(
            ([e]) => {
                if (intersectsBottom(e)) return;
                if (!e.isIntersecting) setPinned(true);
            },
            { threshold: [0.9, 1] }
        );

        const unpinObserver = new IntersectionObserver(
            ([e]) => {
                if (intersectsBottom(e)) return;
                if (e.isIntersecting) setPinned(false);
            },
            { threshold: [0.9, 1] }
        );

        if (pinTriggerRef.current && unpinTriggerRef.current) {
            pinObserver.observe(pinTriggerRef.current);
            unpinObserver.observe(unpinTriggerRef.current);
        }

        return () => {
            pinObserver.disconnect();
            unpinObserver.disconnect();

            featuresRef.current?.removeEventListener(
                "scroll",
                syncProductsScroll
            );
        };
    }, [products, pinTriggerRef, unpinTriggerRef, data]);

    const syncFeaturesScroll = (offset: number, smooth: boolean = false) => {
        const el: HTMLDivElement = document.querySelector(
            ".product-comparison__features"
        )!;

        if (offset > 0) {
            el.scrollLeft = 0;
            return;
        }

        if (smooth) {
            new Tweezer({
                start: el.scrollLeft,
                end: Math.abs(offset),
                duration: 200,
                easing: (t, b, c, d) => {
                    // biome-ignore lint/suspicious/noAssignInExpressions: <TODO>
                    if ((t /= d / 2) < 1) return (c / 2) * t * t + b;
                    return (-c / 2) * (--t * (t - 2) - 1) + b;
                }
            })
                .on("tick", (scrollLeft) => {
                    el.scrollLeft = scrollLeft;
                })
                .begin();
            return;
        }
    };

    const syncProductsScroll = (e: Event) => {
        if (!e.target || !swiper.current || animating.current) {
            return;
        }

        const target = e.target as HTMLDivElement;
        swiper.current.setTranslate(-target.scrollLeft);
        updateButtons();
    };

    const updateButtons = () => {
        if (swiper.current) {
            setAllowPrevious(!swiper.current.isBeginning);
            setAllowNext(!swiper.current.isEnd);
        }
    };

    const removeProduct = (skuToRemove: string) => {
        const newSkuList = skus.filter((sku) => sku !== skuToRemove);

        updateQueryVariable("products", newSkuList.join(","));
        LocalStorage.setItem(
            StorageKeys.ProductComparisonSkuList,
            JSON.stringify(newSkuList)
        );
        setSkus(newSkuList);
        updateButtons();
    };

    const animate = (callback: Function, duration: number = 300) => {
        animating.current = true;
        callback();
        setTimeout(() => {
            animating.current = false;
        }, duration);
    };

    const [containerWidth, setContainerWidth] = useState(0);

    const featuresRefCallback: RefCallback<HTMLDivElement> = (node) => {
        if (!isDefined(node)) return;

        featuresRef.current = node;

        const width = node.clientWidth;
        if (width === containerWidth) return;

        setContainerWidth(node?.clientWidth);
    };

    if (loading || initialLoading) {
        return <ProductComparisonSkeleton />;
    }

    if ((!products.length || !skus?.length) && !loading) {
        return (
            <div className={cn("tw:my-12 tw:text-center")}>
                <p>{t("productComparison.noProductsFound")}</p>
                <Anchor
                    color={"primary"}
                    href={productsPagePath}
                >
                    {t("productComparison.findProducts")}{" "}
                    <i className={cn("la-lg las la-angle-right", "tw:ml-2")} />
                </Anchor>
            </div>
        );
    }

    return (
        <ProductComparisonContextProvider
            allowInstantAddToCart={allowInstantAddToCart}
            differenceOnly={differenceOnly}
            setDifferenceOnly={setDifferenceOnly}
            skus={skus}
            setSkus={setSkus}
        >
            <div className={cn("tw:relative")}>
                <div className={cn("tw:relative")}>
                    <div ref={pinTriggerRef}></div>
                    <div
                        className={cn(
                            "tw:absolute tw:top-0 tw:left-0 tw:h-full tw:w-full tw:pointer-events-none"
                        )}
                    >
                        <div
                            className={cn(
                                "tw:bg-white tw:sticky tw:-top-px tw:flex tw:z-5 tw:scrollbar-none tw:[-ms-overflow-style:none] tw:[pointer-events:all] tw:[&::-webkit-scrollbar]:hidden"
                            )}
                        >
                            <Actions
                                className={cn(
                                    "tw:hidden tw:w-70 tw:absolute tw:bg-white tw:z-2 tw:h-full tw:bottom-4",
                                    "tw:md:[@media(min-height:500px)]:block",
                                    "tw:print:hidden"
                                )}
                            />
                            <div className={cn("tw:w-full")}>
                                <Button
                                    className={cn(
                                        "tw:hidden tw:top-1/2 tw:-translate-y-1/2 tw:absolute tw:z-10 tw:text-xl tw:cursor-pointer tw:my-auto tw:mx-0 tw:transition-all tw:opacity-0 tw:scale-0 tw:origin-[50%_0]",
                                        "tw:md:[@media(min-height:500px)]:block",
                                        "tw:left-72",
                                        {
                                            "tw:opacity-100 tw:scale-100":
                                                allowPrevious
                                        }
                                    )}
                                    type={"button"}
                                    color={"gray"}
                                    onClick={() =>
                                        animate(() =>
                                            swiper.current?.slidePrev()
                                        )
                                    }
                                    aria-label={t(
                                        "accessibility.slider.previous"
                                    )}
                                >
                                    <i className={cn("las la-angle-left")} />
                                </Button>
                                <Button
                                    className={cn(
                                        "tw:hidden tw:top-1/2 tw:-translate-y-1/2 tw:absolute tw:z-10 tw:text-xl tw:cursor-pointer tw:my-auto tw:mx-0 tw:transition-all tw:opacity-0 tw:scale-0 tw:origin-[50%_0]",
                                        "tw:md:[@media(min-height:500px)]:block",
                                        "tw:right-2",
                                        {
                                            "tw:opacity-100 tw:scale-100":
                                                allowNext
                                        }
                                    )}
                                    type={"button"}
                                    color={"gray"}
                                    onClick={() =>
                                        animate(() =>
                                            swiper.current!.slideNext()
                                        )
                                    }
                                    aria-label={t("accessibility.slider.next")}
                                >
                                    <i className={cn("las la-angle-right")} />
                                </Button>
                                <Swiper
                                    modules={[FreeMode, Mousewheel]}
                                    updateOnWindowResize={true}
                                    onSlideChange={() => updateButtons()}
                                    freeMode={{
                                        enabled: true,
                                        sticky: false,
                                        momentumBounce: false,
                                        momentum: false
                                    }}
                                    slidesPerView={"auto"}
                                    onTouchMove={(e) =>
                                        syncFeaturesScroll(e.translate)
                                    }
                                    onProgress={(e) =>
                                        syncFeaturesScroll(e.translate)
                                    }
                                    onTransitionStart={(e) => {
                                        const swiper = e as SwiperCore;
                                        syncFeaturesScroll(
                                            swiper.translate,
                                            true
                                        );
                                        updateButtons();
                                    }}
                                    watchSlidesProgress={true}
                                    speed={200}
                                    mousewheel={{
                                        sensitivity: 1,
                                        forceToAxis: true
                                    }}
                                    onSwiper={(s) => {
                                        swiper.current = s;
                                        updateButtons();
                                    }}
                                >
                                    <SwiperSlide
                                        className={cn(
                                            "tw:hidden tw:items-end tw:mt-auto tw:w-70",
                                            "tw:md:[@media(min-height:500px)]:flex"
                                        )}
                                    >
                                        <div></div>
                                    </SwiperSlide>
                                    {products.map((product) => (
                                        <SwiperSlide
                                            data-product-tile-sku={product.sku}
                                            key={product.sku}
                                            className={cn(
                                                "tw:transition-[height] tw:self-stretch tw:mt-4 tw:flex tw:relative tw:flex-col tw:justify-start tw:w-50 tw:h-90",
                                                "tw:md:[@media(min-height:500px)]:w-70 tw:md:[@media(min-height:500px)]:h-105",
                                                "tw:not-last:border-r tw:not-last:border-grey-light",
                                                "tw:[&_.productTile]:mb-4 tw:[&_.productTile]:w-full tw:[&_.productTile_*]:transition-all tw:[&_.productTile__title]:h-10! tw:[&_.productTile--row--PromotionLabelSmall]:h-16 tw:[&_.productTile--row--PriceSmall]:h-16 tw:[&_.productTile--row--PriceMedium]:h-16 tw:[&_.productTile--row--PriceLarge]:h-16 tw:[&_.productTile--row--PriceRibbon]:h-16",
                                                {
                                                    "tw:justify-start tw:h-65 tw:md:[@media(min-height:500px)]:h-85":
                                                        pinned,
                                                    "tw:[&_.productTile--row--PromotionLabelSmall]:h-0! tw:[&_.productTile--row--PromotionLabelSmall]:opacity-0! tw:[.productTile--row--PromotionLabelSmall]:m-0! tw:[&_.productTile--row--Reviews]:h-0! tw:[&_.productTile--row--Reviews]:opacity-0! tw:[.productTile--row--Reviews]:m-0!":
                                                        pinned
                                                }
                                            )}
                                        >
                                            <Button
                                                onClick={() =>
                                                    removeProduct(product.sku)
                                                }
                                                className={cn(
                                                    "tw:aspect-square tw:absolute tw:top-[0.2rem] tw:left-[0.2rem] tw:z-1",
                                                    {
                                                        "tw:hidden": pinned
                                                    }
                                                )}
                                                color={"transparent"}
                                                type={"button"}
                                                aria-label={t(
                                                    "accessibility.comparison.remove"
                                                )}
                                            >
                                                <i
                                                    className={cn(
                                                        "las la-times"
                                                    )}
                                                />
                                            </Button>
                                            <ProductTile
                                                sku={product.sku}
                                                product={product}
                                                allowInstantAddToCart={
                                                    allowInstantAddToCart
                                                }
                                                productTileConfig={{
                                                    imageRenditions:
                                                        ProductTileRenditions,
                                                    border: false,
                                                    amountOfFeatures: 0,
                                                    tilePadding: true,
                                                    components:
                                                        ProductTileLayout.COMPARISON()
                                                }}
                                            />
                                        </SwiperSlide>
                                    ))}
                                </Swiper>
                            </div>
                        </div>
                    </div>
                    <div ref={unpinTriggerRef}></div>
                    <div
                        className={cn(
                            "tw:overflow-auto tw:flex tw:overscroll-x-none tw:pt-95",
                            "tw:md:[@media(min-height:500px)]:pt-105",
                            "tw:print:pt-[calc(323-3rem)]"
                        )}
                        ref={featuresRefCallback}
                    >
                        <div
                            className={cn(
                                "tw:grow-0 tw:shrink-0 tw:min-w-full"
                            )}
                        >
                            <Actions
                                className={cn(
                                    "tw:block",
                                    "tw:md:[@media(min-height:500px)]:hidden",
                                    "tw:print:hidden"
                                )}
                            />
                            <Features
                                products={products}
                                width={containerWidth}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </ProductComparisonContextProvider>
    );
};

export { ProductComparisonContainer };
